import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2, AlertOctagon, MailCheck, Sparkles, Eye, EyeOff } from "lucide-react";
import TurnstileField, { turnstileEnabled } from "./TurnstileField";

const API = process.env.REACT_APP_BACKEND_URL;
const AX  = axios.create({ baseURL: API, withCredentials: true });

/**
 * SignInForm — shared credentials form used by both /signin (full page)
 * and <SignInModal> (popover). Renders the email/password fields, the
 * "magic link" toggle + sent state, "Trust this device for 30 days",
 * forgot-password link, and the signup ↔ login mode switch.
 *
 * Form-only — does NOT render its own card chrome or H1. Wrap it in
 * a card and set the surrounding title/subtitle from the parent so
 * each context can style its own header.
 *
 * Props
 *   testidPrefix       string – prefix for all data-testids ("signin" | "signin-modal")
 *   mode               "login" | "signup"
 *   setMode            (next) => void  – called when the user toggles signup/login
 *   onLogin            async ({email,password}) => void  – password login
 *   onSignupSubmit     async ({email,password,name,marketing_opt_in}) => void
 *                                         – called after signup-step-1 form is valid;
 *                                           wrapper handles persona/account creation.
 *   onMagicTrusted     () => void         – called when magic-link request returns
 *                                           {signed_in_instantly:true}.
 *   magicError         string             – optional magic-link URL param error
 *   onForgotClick      () => void         – optional click handler on "Forgot password?"
 *   onFreescanClick    () => void         – optional click handler on "Or run a free scan"
 *   autoFocusEmail     bool               – default false
 *   signupSubmitLabel  string             – "Continue" or "Create account"
 *   className          string             – extra classes on root div
 */
export default function SignInForm({
  testidPrefix = "signin",
  mode,
  setMode,
  onLogin,
  onSignupSubmit,
  onMagicTrusted,
  magicError = "",
  onForgotClick,
  onFreescanClick,
  autoFocusEmail = false,
  signupSubmitLabel = "Continue",
  className = "",
}) {
  const tid = (suffix) => `${testidPrefix}-${suffix}`;
  const [form, setForm] = useState({ email: "", password: "", name: "", marketing_opt_in: true });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [authMethod, setAuthMethod] = useState("password");
  const [magicSent, setMagicSent]   = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [tsToken, setTsToken] = useState("");
  // Iter273.2 — Show/hide password toggle (beta-tester request).
  const [showPw, setShowPw] = useState(false);
  // Iter276.2 — Beta-tester (Sergio) reproduction: iOS Safari + Apple
  // Passwords autofill sometimes assigns input.value via raw DOM
  // mutation WITHOUT firing React's synthetic onChange, leaving the
  // controlled-input state empty even though the field LOOKS filled.
  // Refs let us read the actual DOM value at submit time as a
  // fallback so the bcrypt comparison never gets "" against a real
  // hash. Also: a sticky "refreshing security check" label so users
  // see Turnstile re-issuing a fresh token after a failed attempt
  // (parity with admin login from Iter273.1).
  const emailRef = React.useRef(null);
  const pwRef    = React.useRef(null);
  const [justFailed, setJustFailed] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Iter276.2 — Read the DOM value as a fallback. iOS / Chrome
    // password-manager autofill on a controlled <input> can bypass
    // React's onChange — when that happens, form.password is "" but
    // pwRef.current.value has the real autofilled string.
    const liveEmail =
      (form.email && form.email.trim())
      || (emailRef.current && emailRef.current.value && emailRef.current.value.trim())
      || "";
    const livePassword =
      (form.password)
      || (pwRef.current && pwRef.current.value)
      || "";

    // Iter276.2 — Turnstile token guard. The customer-side form
    // previously allowed submit-with-empty-token because the widget
    // takes 100-500ms to re-issue after the page loads or after a
    // failed attempt. Beta tester reported "Bot challenge failed
    // (missing_token)" — exactly this race. Mirror the admin-login
    // guard from Iter273.1.
    if (turnstileEnabled() && !tsToken && !(mode === "login" && authMethod === "magic")) {
      setError("Waiting for the security check — give it a second, then try again.");
      return;
    }

    setError(""); setBusy(true);
    try {
      if (mode === "login" && authMethod === "magic") {
        const { data } = await AX.post("/api/account/magic-link/request",
                      { email: liveEmail.toLowerCase(),
                        trust_device: trustDevice,
                        turnstile_token: tsToken });
        if (data?.signed_in_instantly) {
          onMagicTrusted?.();
          return;
        }
        setMagicSent(true);
        return;
      }
      if (mode === "login") {
        await onLogin({ email: liveEmail, password: livePassword,
                        turnstile_token: tsToken });
      } else {
        if (!liveEmail || !livePassword) {
          throw new Error("Email and password are required.");
        }
        await onSignupSubmit({
          email: liveEmail,
          password: livePassword,
          name: form.name,
          marketing_opt_in: form.marketing_opt_in,
          turnstile_token: tsToken,
        });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong.");
      // Iter276.2 — Cloudflare Turnstile tokens are single-use; replaying
      // the same one on a retry returns `timeout-or-duplicate`, so an
      // immediate retry would surface a confusing "Bot challenge failed"
      // on top of whatever the real error was. Force a fresh token.
      setTsToken("");
      setJustFailed(true);
      setTimeout(() => setJustFailed(false), 1500);
    } finally {
      setBusy(false);
    }
  };

  if (mode === "login" && magicSent) {
    return (
      <div className={`mt-5 ${className}`} data-testid={tid("magic-sent")}>
        <div className="px-4 py-4 rounded inline-flex items-center gap-2"
             style={{ background: "rgba(31,221,131,0.08)",
                      border: "1px solid rgba(31,221,131,0.30)",
                      color: "#1FDD83", fontSize: 13 }}>
          <MailCheck size={15} /> Check your inbox
        </div>
        <p className="mt-4 text-sm" style={{ color: "var(--qt-text-2)" }}>
          If an account exists for{" "}
          <strong style={{ color: "#fff" }}>{form.email}</strong>, we just
          emailed you a sign-in link. It expires in 15 minutes and can
          only be used once.
        </p>
        <p className="mt-3 text-[12.5px]" style={{ color: "var(--qt-text-3)" }}>
          Didn't get it? Check spam, then try again in a few minutes. If
          you're stuck, contact{" "}
          <a href="mailto:support@qortrace.com"
             style={{ color: "var(--qt-cyan)" }}>support@qortrace.com</a>.
        </p>
        <button type="button"
                data-testid={tid("magic-sent-back")}
                onClick={() => { setMagicSent(false); setAuthMethod("password"); }}
                className="qt-mono text-[11px] mt-5 px-3 py-1.5 rounded"
                style={{ background: "transparent",
                         border: "1px solid var(--qt-border)",
                         color: "var(--qt-text-2)",
                         letterSpacing: "0.06em", cursor: "pointer" }}>
          ← Try a different method
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit}
          className={`mt-5 grid grid-cols-1 gap-3 ${className}`}
          data-testid={tid("form")}>
      {mode === "signup" && (
        <div>
          <div className="qt-label mb-2">YOUR NAME (OPTIONAL)</div>
          <input className="qt-input" value={form.name} data-testid={tid("name")}
                 autoComplete="name"
                 onChange={(e) => set("name", e.target.value)} />
        </div>
      )}
      <div>
        <div className="qt-label mb-2">EMAIL</div>
        <input className="qt-input" required value={form.email} type="email"
               ref={emailRef}
               autoFocus={autoFocusEmail}
               // Iter276.2 — autoComplete="username" is the canonical
               // hint password managers (Apple Passwords, 1Password,
               // Bitwarden, Chrome PM) use to pair the email field
               // with the password field for autofill. Without it,
               // some iOS Safari flows skip the synthetic onChange
               // event entirely.
               autoComplete={mode === "signup" ? "email" : "username"}
               inputMode="email"
               name="email"
               data-testid={tid("email")} placeholder="you@protocol.xyz"
               onChange={(e) => set("email", e.target.value)}
               // Iter276.2 — onInput catches autofills that don't
               // dispatch a synthetic React onChange (iOS Safari +
               // Apple Passwords). Without this, the controlled-input
               // state stayed "" even though the field was visibly
               // filled, which is what was causing Sergio's "Invalid
               // email or password" lockout on every account.
               onInput={(e) => set("email", e.target.value)} />
      </div>
      {!(mode === "login" && authMethod === "magic") && (
        <div>
          <div className="qt-label mb-2">PASSWORD</div>
          <div style={{ position: "relative" }}>
            <input type={showPw ? "text" : "password"} className="qt-input" required value={form.password}
                   ref={pwRef}
                   data-testid={tid("password")}
                   // Iter276.2 — current-password vs new-password is
                   // what tells Apple Passwords whether to autofill
                   // existing credentials or generate a fresh one.
                   autoComplete={mode === "signup" ? "new-password" : "current-password"}
                   name="password"
                   placeholder={mode === "signup" ? "9+ chars · upper/lower/number/special" : "Your password"}
                   onChange={(e) => set("password", e.target.value)}
                   // Iter276.2 — fallback for autofill events that
                   // bypass React's synthetic onChange. See email
                   // input above for context.
                   onInput={(e) => set("password", e.target.value)}
                   style={{ paddingRight: 40 }} />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Hide password" : "Show password"}
              data-testid={tid("password-eye")}
              tabIndex={-1}
              style={{
                position: "absolute", right: 8, top: "50%",
                transform: "translateY(-50%)",
                background: "transparent", border: "none",
                color: "var(--qt-text-3, #A5B5CE)",
                padding: 6, cursor: "pointer", borderRadius: 6,
                display: "inline-flex", alignItems: "center",
              }}
            >
              {showPw ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
            </button>
          </div>
        </div>
      )}
      {mode === "login" && authMethod === "magic" && (
        <label className="flex items-start gap-2 text-xs select-none"
               style={{ color: "var(--qt-text-2)", cursor: "pointer" }}>
          <input type="checkbox" checked={trustDevice}
                 data-testid={tid("trust-device")}
                 onChange={(e) => setTrustDevice(e.target.checked)}
                 style={{ marginTop: 3 }} />
          <span>
            Trust this device for 30 days — next time you sign in here we'll
            log you in instantly without sending an email.
          </span>
        </label>
      )}
      {mode === "signup" && (
        <div className="flex items-start gap-2 text-xs" style={{ color: "var(--qt-text-3)" }}>
          <input type="checkbox" id={`${testidPrefix}-opt-in`} checked={form.marketing_opt_in}
                 data-testid={tid("optin")}
                 onChange={(e) => set("marketing_opt_in", e.target.checked)} style={{ marginTop: 3 }} />
          <label htmlFor={`${testidPrefix}-opt-in`}>
            I want to receive my free preview report by email + occasional QorTrace updates.
            You can unsubscribe one-click from any email.
          </label>
        </div>
      )}
      {magicError && mode === "login" && (
        <div className="px-3 py-2 rounded text-sm"
             data-testid={tid("magic-error")}
             style={{ background: "rgba(255,176,0,0.08)",
                      border: "1px solid rgba(255,176,0,0.40)",
                      color: "#FFB000" }}>
          <AlertOctagon size={14} className="inline -mt-1 mr-2" />
          {magicError === "expired_or_used"
            ? "That sign-in link is expired or already used. Send a fresh one below."
            : "Sign-in link was invalid. Try again."}
        </div>
      )}
      {error && (
        <div className="px-3 py-2 rounded text-sm"
             data-testid={tid("error")}
             style={{ background: "rgba(255,42,42,0.08)", border: "1px solid rgba(255,42,42,0.4)", color: "#FF6E6E" }}>
          <AlertOctagon size={14} className="inline -mt-1 mr-2" />{error}
        </div>
      )}
      {/* Phase 12.1a + 12.2 — Cloudflare Turnstile. Rendered for
          every mode (login/signup/magic-link request) so bots can't
          flood any credential or magic-link surface. */}
      <TurnstileField
        action={
          mode === "signup"
            ? "signup"
            : (mode === "login" && authMethod === "magic")
              ? "magic_link"
              : "login"
        }
        onToken={setTsToken}
      />
      <button type="submit"
              disabled={busy || (turnstileEnabled() && !tsToken && !(mode === "login" && authMethod === "magic")) || justFailed}
              data-testid={tid("submit")}
              className="qt-btn qt-btn-primary w-full justify-center mt-1">
        {busy
          ? <><Loader2 size={14} className="animate-spin" /> Working…</>
          : (justFailed || (turnstileEnabled() && !tsToken && !(mode === "login" && authMethod === "magic")))
            ? "REFRESHING SECURITY CHECK…"
            : mode === "signup"
              ? <>{signupSubmitLabel} <ArrowRight size={14} /></>
              : authMethod === "magic"
                ? <><Sparkles size={13} /> Email me a sign-in link</>
                : <>Sign in <ArrowRight size={14} /></>}
      </button>
      {/* Iter276.5 — Only surface the "waiting for security check…"
          warning paragraph AFTER a failed attempt (justFailed). On
          initial page load the Turnstile widget takes 200-500ms to
          mint a token; showing the scary copy during that window
          confused first-time visitors. The button itself still
          flips to "REFRESHING SECURITY CHECK…" so the user knows
          submit is gated, but we no longer claim something is wrong
          unless something actually is. */}
      {justFailed && !busy && (
        <p data-testid={tid("turnstile-refreshing")}
           className="text-xs text-center mt-1"
           style={{ color: "var(--qt-text-3)" }}>
          Waiting for security check… if this takes more than 10 seconds,
          refresh the page.
        </p>
      )}

      {mode === "login" && (
        <button type="button"
                data-testid={tid("toggle-magic")}
                onClick={() => { setAuthMethod(authMethod === "magic" ? "password" : "magic"); setError(""); }}
                className="qt-mono text-[11px] mt-1 px-2 py-1.5 rounded inline-flex items-center justify-center gap-1.5 w-full"
                style={{ background: "transparent",
                         border: "1px dashed var(--qt-border)",
                         color: "var(--qt-text-2)",
                         letterSpacing: "0.06em", cursor: "pointer" }}>
          {authMethod === "magic"
            ? <>← Sign in with password instead</>
            : <><MailCheck size={11} /> Or sign in via magic link (no password)</>}
        </button>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 mt-2 text-xs">
        <button type="button"
                onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setError(""); }}
                data-testid={tid("toggle")}
                className="qt-mono" style={{ color: "var(--qt-cyan)", letterSpacing: "0.05em" }}>
          {mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
        </button>
        <div className="flex items-center gap-3">
          {mode === "login" && (
            <Link to="/account/forgot-password" onClick={onForgotClick}
                  data-testid={tid("forgot-link")}
                  className="qt-mono"
                  style={{ color: "var(--qt-text-2)", letterSpacing: "0.05em" }}>
              Forgot password?
            </Link>
          )}
          <Link to="/free-scan" onClick={onFreescanClick}
                data-testid={tid("freescan-link")}
                className="qt-mono" style={{ color: "var(--qt-text-3)", letterSpacing: "0.05em" }}>
            Or run a free scan →
          </Link>
        </div>
      </div>
    </form>
  );
}
